import type { Editor } from "@tiptap/react";
import type { Node } from "@tiptap/pm/model";
import { Selection, type EditorState, type Transaction } from "@tiptap/pm/state";
import { CellSelection, TableMap, cellAround, findTable, selectedRect, selectionCell } from "@tiptap/pm/tables";
import type { FindNodeResult, Rect } from "@tiptap/pm/tables";
import { Mapping } from "@tiptap/pm/transform";

// Ported from the mnemos table kit (itself the official Tiptap table template):
// pure ProseMirror geometry/selection helpers the handles are built on.

export const EMPTY_CELL_WIDTH = 120;
export const EMPTY_CELL_HEIGHT = 40;

export type Orientation = "column" | "row";

export interface CellInfo extends FindNodeResult {
  column: number;
  row: number;
}

export type CellCoordinates = { col: number; row: number };

export type TableInfo = FindNodeResult & { map: TableMap };

const EMPTY_CELLS_RESULT = { cells: [], mergedCells: [] };

export function isHTMLElement(value: unknown): value is HTMLElement {
  return value instanceof HTMLElement;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

export function safeClosest<T extends Element>(start: Element | null, selector: string): T | null {
  return (start?.closest?.(selector) as T | null) ?? null;
}

export type DomCellAroundResult = {
  domNode: HTMLElement;
  tbodyNode: HTMLTableSectionElement | null;
  type: "cell" | "wrapper";
};

// Walk up from an element until we find a TD/TH or the table wrapper.
export function domCellAround(target: Element): DomCellAroundResult | undefined {
  let current: Element | null = target;

  while (current && current.tagName !== "TD" && current.tagName !== "TH" && !current.classList.contains("tableWrapper")) {
    if (current.classList.contains("ProseMirror")) return undefined;
    current = isHTMLElement(current.parentNode) ? (current.parentNode as Element) : null;
  }

  if (!current) return undefined;

  if (current.tagName === "TD" || current.tagName === "TH") {
    return {
      domNode: current as HTMLElement,
      tbodyNode: safeClosest<HTMLTableSectionElement>(current, "tbody"),
      type: "cell",
    };
  }
  return {
    domNode: current as HTMLElement,
    tbodyNode: (current as HTMLElement).querySelector("tbody"),
    type: "wrapper",
  };
}

export function isTableNode(node: Node | null | undefined): node is Node {
  return !!node && (node.type.name === "table" || node.type.spec.tableRole === "table");
}

function isWithinBounds(row: number, col: number, map: TableMap): boolean {
  return row >= 0 && row < map.height && col >= 0 && col < map.width;
}

// Table at an exact position, or the one containing the selection.
export function getTable(editor: Editor | null, tablePos?: number): TableInfo | null {
  if (!editor) return null;

  let table: FindNodeResult | null = null;

  if (typeof tablePos === "number") {
    const tableNode = editor.state.doc.nodeAt(tablePos);
    if (tableNode?.type.name === "table") {
      table = {
        depth: editor.state.doc.resolve(tablePos).depth,
        node: tableNode,
        pos: tablePos,
        start: tablePos + 1,
      };
    }
  }

  if (!table) {
    const $from = editor.state.doc.resolve(editor.state.selection.from);
    table = findTable($from) ?? null;
  }
  if (!table) return null;

  const map = TableMap.get(table.node);
  return map ? { ...table, map } : null;
}

export function isCellMerged(node: Node | null): boolean {
  if (!node) return false;
  return (node.attrs.colspan ?? 1) > 1 || (node.attrs.rowspan ?? 1) > 1;
}

function resolveOrientationIndex(
  state: EditorState,
  table: TableInfo,
  orientation: Orientation,
  providedIndex?: number,
): number | null {
  if (typeof providedIndex === "number") return providedIndex;

  if (state.selection instanceof CellSelection) {
    const rect = selectedRect(state);
    return orientation === "row" ? rect.top : rect.left;
  }

  const $cell = cellAround(state.selection.$anchor) ?? selectionCell(state);
  if (!$cell) return null;

  const rect = table.map.findCell($cell.pos - table.start);
  return orientation === "row" ? rect.top : rect.left;
}

function createCellInfo(row: number, column: number, cellPos: number, cellNode: Node): CellInfo {
  return {
    column,
    depth: cellNode.content.size,
    node: cellNode,
    pos: cellPos,
    row,
    start: cellPos + 1,
  };
}

function collectCells(
  editor: Editor | null,
  orientation: Orientation,
  index?: number,
  tablePos?: number,
): { cells: CellInfo[]; mergedCells: CellInfo[] } {
  if (!editor) return EMPTY_CELLS_RESULT;

  const table = getTable(editor, tablePos);
  if (!table) return EMPTY_CELLS_RESULT;

  const resolvedIndex = resolveOrientationIndex(editor.state, table, orientation, index);
  if (resolvedIndex === null) return EMPTY_CELLS_RESULT;

  const maxIndex = orientation === "row" ? table.map.height : table.map.width;
  if (resolvedIndex < 0 || resolvedIndex >= maxIndex) return EMPTY_CELLS_RESULT;

  const cells: CellInfo[] = [];
  const mergedCells: CellInfo[] = [];
  const seenMerged = new Set<number>();
  const iterationCount = orientation === "row" ? table.map.width : table.map.height;

  for (let i = 0; i < iterationCount; i += 1) {
    const row = orientation === "row" ? resolvedIndex : i;
    const col = orientation === "row" ? i : resolvedIndex;
    const mapCell = table.map.map[row * table.map.width + col];
    if (mapCell === undefined) continue;

    const cellPos = table.start + mapCell;
    const cellNode = table.node.nodeAt(mapCell);
    if (!cellNode) continue;

    const cell = createCellInfo(row, col, cellPos, cellNode);
    if (isCellMerged(cellNode) && !seenMerged.has(cellPos)) {
      mergedCells.push(cell);
      seenMerged.add(cellPos);
    }
    cells.push(cell);
  }

  return { cells, mergedCells };
}

export function getRowCells(editor: Editor | null, rowIndex?: number, tablePos?: number) {
  return collectCells(editor, "row", rowIndex, tablePos);
}

export function getColumnCells(editor: Editor | null, columnIndex?: number, tablePos?: number) {
  return collectCells(editor, "column", columnIndex, tablePos);
}

export function getRowOrColumnCells(editor: Editor | null, index: number, orientation: Orientation, tablePos?: number) {
  return collectCells(editor, orientation, index, tablePos);
}

// Cells overlap a rectangle when any cell in it is merged with cells outside.
export function cellsOverlapRectangle({ height, map, width }: TableMap, rect: Rect) {
  let indexTop = rect.top * width + rect.left;
  let indexLeft = indexTop;
  let indexBottom = (rect.bottom - 1) * width + rect.left;
  let indexRight = indexTop + (rect.right - rect.left - 1);
  for (let i = rect.top; i < rect.bottom; i += 1) {
    if (
      (rect.left > 0 && map[indexLeft] === map[indexLeft - 1]) ||
      (rect.right < width && map[indexRight] === map[indexRight + 1])
    ) {
      return true;
    }
    indexLeft += width;
    indexRight += width;
  }
  for (let i = rect.left; i < rect.right; i += 1) {
    if (
      (rect.top > 0 && map[indexTop] === map[indexTop - width]) ||
      (rect.bottom < height && map[indexBottom] === map[indexBottom + width])
    ) {
      return true;
    }
    indexTop += 1;
    indexBottom += 1;
  }
  return false;
}

// Run a mutation and put the caret back where it was (mapped through the changes).
export function runPreservingCursor(editor: Editor, fn: () => void): boolean {
  const view = editor.view;
  const bookmark = view.state.selection.getBookmark();
  const startFrom = view.state.selection.from;

  const mapping = new Mapping();
  const originalDispatch = view.dispatch.bind(view);
  view.dispatch = (tr: Transaction) => {
    mapping.appendMapping(tr.mapping);
    originalDispatch(tr);
  };
  try {
    fn();
  } finally {
    view.dispatch = originalDispatch;
  }

  try {
    const sel = bookmark.map(mapping).resolve(view.state.doc);
    view.dispatch(view.state.tr.setSelection(sel));
    return true;
  } catch {
    const mappedPos = mapping.map(startFrom, -1);
    const clamped = clamp(mappedPos, 0, view.state.doc.content.size);
    view.dispatch(view.state.tr.setSelection(Selection.near(view.state.doc.resolve(clamped), -1)));
    return false;
  }
}

export function isCellEmpty(cellNode: Node): boolean {
  if (cellNode.childCount === 0) return true;
  let isEmpty = true;
  cellNode.descendants((node) => {
    if (node.isText && node.text?.trim()) {
      isEmpty = false;
      return false;
    }
    if (node.isLeaf && !node.isText) {
      isEmpty = false;
      return false;
    }
    return true;
  });
  return isEmpty;
}

function countEmptyCellsFromEnd(editor: Editor, tablePos: number, orientation: Orientation): number {
  const table = getTable(editor, tablePos);
  if (!table) return 0;

  const { doc } = editor.state;
  const maxIndex = orientation === "row" ? table.map.height : table.map.width;
  let emptyCount = 0;

  for (let idx = maxIndex - 1; idx >= 0; idx -= 1) {
    const seen = new Set<number>();
    let isLineEmpty = true;
    const iterationCount = orientation === "row" ? table.map.width : table.map.height;

    for (let i = 0; i < iterationCount; i += 1) {
      const row = orientation === "row" ? idx : i;
      const col = orientation === "row" ? i : idx;
      const rel = table.map.positionAt(row, col, table.node);
      if (seen.has(rel)) continue;
      seen.add(rel);

      const cell = doc.nodeAt(tablePos + 1 + rel);
      if (cell && !isCellEmpty(cell)) {
        isLineEmpty = false;
        break;
      }
    }

    if (isLineEmpty) emptyCount += 1;
    else break;
  }
  return emptyCount;
}

export function countEmptyRowsFromEnd(editor: Editor, tablePos: number): number {
  return countEmptyCellsFromEnd(editor, tablePos, "row");
}

export function countEmptyColumnsFromEnd(editor: Editor, tablePos: number): number {
  return countEmptyCellsFromEnd(editor, tablePos, "column");
}

// Rounding with a dead-zone around integer boundaries; keeps drag-resize calm.
export function marginRound(num: number, margin = 0.3): number {
  const floor = Math.floor(num);
  const ceil = Math.ceil(num);
  if (num < floor + margin) return floor;
  if (num > ceil - margin) return ceil;
  return Math.round(num);
}

// CellSelection over the bounding box of the given coordinates. Returns the
// state with the selection applied (for feeding prosemirror-tables commands)
// or dispatches it when a dispatch is provided.
export function selectCellsByCoords(
  editor: Editor | null,
  tablePos: number,
  coords: CellCoordinates[],
  dispatch?: (tr: Transaction) => void,
): EditorState | null {
  if (!editor) return null;
  const table = getTable(editor, tablePos);
  if (!table) return null;

  const { state } = editor;
  const tableMap = table.map;

  const cleaned = coords
    .map((coord) => ({
      col: clamp(coord.col, 0, tableMap.width - 1),
      row: clamp(coord.row, 0, tableMap.height - 1),
    }))
    .filter((coord) => isWithinBounds(coord.row, coord.col, tableMap));
  if (cleaned.length === 0) return null;

  const rows = cleaned.map((coord) => coord.row);
  const cols = cleaned.map((coord) => coord.col);
  const topRow = Math.min(...rows);
  const bottomRow = Math.max(...rows);
  const leftCol = Math.min(...cols);
  const rightCol = Math.max(...cols);

  const cellPosFromMap = (row: number, col: number): number | null => {
    const offset = tableMap.map[row * tableMap.width + col];
    return offset === undefined ? null : tablePos + 1 + offset;
  };

  const anchorPosition = cellPosFromMap(topRow, leftCol);
  if (anchorPosition === null) return null;

  let headPosition = cellPosFromMap(bottomRow, rightCol);
  if (headPosition === null) return null;

  // Merged-cell edge case: anchor and head may resolve to the same cell.
  if (headPosition === anchorPosition) {
    outer: for (let row = bottomRow; row >= topRow; row -= 1) {
      for (let col = rightCol; col >= leftCol; col -= 1) {
        const candidate = cellPosFromMap(row, col);
        if (candidate !== null && candidate !== anchorPosition) {
          headPosition = candidate;
          break outer;
        }
      }
    }
  }

  try {
    const selection = new CellSelection(state.doc.resolve(anchorPosition), state.doc.resolve(headPosition));
    const transaction = state.tr.setSelection(selection);
    if (dispatch) {
      dispatch(transaction);
      return null;
    }
    return state.apply(transaction);
  } catch {
    return null;
  }
}

export function getIndexCoordinates(
  editor: Editor | null,
  index: number,
  orientation: Orientation,
  tablePos?: number,
): CellCoordinates[] | null {
  if (!editor) return null;
  const table = getTable(editor, tablePos);
  if (!table) return null;

  const { height, width } = table.map;
  if (index < 0) return null;
  if (orientation === "row" && index >= height) return null;
  if (orientation === "column" && index >= width) return null;

  return orientation === "row"
    ? Array.from({ length: width }, (_, col) => ({ col, row: index }))
    : Array.from({ length: height }, (_, row) => ({ col: index, row }));
}

// Re-select the row/column after an action so feedback stays visible.
export function updateSelectionAfterAction(editor: Editor, orientation: Orientation, newIndex: number, tablePos?: number): void {
  try {
    const table = getTable(editor, tablePos);
    if (!table) return;

    const { map } = table;
    const maxIndex = orientation === "row" ? map.height : map.width;
    if (newIndex < 0 || newIndex >= maxIndex) return;

    const startCellPos =
      table.start + (orientation === "row" ? map.positionAt(newIndex, 0, table.node) : map.positionAt(0, newIndex, table.node));
    const endCellPos =
      table.start +
      (orientation === "row"
        ? map.positionAt(newIndex, map.width - 1, table.node)
        : map.positionAt(map.height - 1, newIndex, table.node));

    const { state } = editor;
    const selection = CellSelection.create(state.doc, startCellPos, endCellPos);
    editor.view.dispatch(state.tr.setSelection(selection));
  } catch {
    // A vanished cell just means we skip the highlight.
  }
}

export function selectCellAt(editor: Editor | null, row: number, col: number, tablePos?: number): boolean {
  if (!editor) return false;
  const table = getTable(editor, tablePos);
  if (!table || !isWithinBounds(row, col, table.map)) return false;

  const absCellPos = table.start + table.map.positionAt(row, col, table.node);
  const $cell = cellAround(editor.state.doc.resolve(absCellPos));
  const selection = CellSelection.create(editor.state.doc, $cell ? $cell.pos : absCellPos);
  editor.view.dispatch(editor.state.tr.setSelection(selection));
  return true;
}

// Bottom-left cell for rows, top-right for columns — the cell the extend
// buttons append after.
export function selectLastCell(editor: Editor, tableNode: Node, tablePos: number, orientation: Orientation): boolean {
  const map = TableMap.get(tableNode);
  const isRow = orientation === "row";
  const row = isRow ? map.height - 1 : 0;
  const col = isRow ? 0 : map.width - 1;

  const cellPos = map.map[row * map.width + col];
  if (cellPos === undefined) return false;

  const cellIndex = map.map.indexOf(cellPos);
  const actualRow = cellIndex >= 0 ? Math.floor(cellIndex / map.width) : 0;
  const actualCol = cellIndex >= 0 ? cellIndex % map.width : 0;
  return selectCellAt(editor, actualRow, actualCol, tablePos);
}

export function getCellIndicesFromDOM(
  cell: HTMLTableCellElement,
  tableNode: Node | null,
  editor: Editor,
): { colIndex: number; rowIndex: number } | null {
  if (!tableNode) return null;
  try {
    const cellPos = editor.view.posAtDOM(cell, 0);
    const $cellPos = editor.view.state.doc.resolve(cellPos);

    for (let d = $cellPos.depth; d > 0; d -= 1) {
      const node = $cellPos.node(d);
      if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
        const tableMap = TableMap.get(tableNode);
        const cellOffset = $cellPos.before(d) - $cellPos.start(d - 2);
        const cellIndex = tableMap.map.indexOf(cellOffset);
        if (cellIndex < 0) return null;
        return {
          colIndex: cellIndex % tableMap.width,
          rowIndex: Math.floor(cellIndex / tableMap.width),
        };
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function getTableFromDOM(tableElement: HTMLElement, editor: Editor): { node: Node; pos: number } | null {
  try {
    const pos = editor.view.posAtDOM(tableElement, 0);
    const $pos = editor.view.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 0; d -= 1) {
      const node = $pos.node(d);
      if (isTableNode(node)) {
        return { node, pos: d === 0 ? 0 : $pos.before(d) };
      }
    }
  } catch {
    return null;
  }
  return null;
}
